import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Plus, Landmark, Mail, CheckCircle2, XCircle, Info } from "lucide-react";
import { toast } from "sonner";
import { Kpi, Section, Empty, Pill, PageTitle, fmtDateTime, fmtDate } from "@/components/services/Shared";

export const SEVERITY_COLOR = { faible: "#2E7D32", modere: "#E3A72F", eleve: "#D96C4A", critique: "#C62828" };
export const SEVERITY_LABEL = { faible: "Faible", modere: "Modéré", eleve: "Élevé", critique: "Critique" };
export const ALERT_STATUS = { open: ["Ouverte — en attente DPS", "#C62828"], acknowledged: ["Prise en compte par le Ministère", "#E3A72F"], closed: ["Clôturée", "#2E7D32"] };

export function AlertCard({ a, actions }) {
  const [st, col] = ALERT_STATUS[a.status] || [a.status, "#5C6B61"];
  return (
    <div className="py-4 flex gap-4 flex-wrap" data-testid={`alert-card-${a.id}`}>
      <div className="w-2 self-stretch rounded-full" style={{ background: SEVERITY_COLOR[a.severity] }} />
      <div className="flex-1 min-w-[240px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-[Outfit] font-bold text-lg">{a.disease_label}</span>
          <Pill color={SEVERITY_COLOR[a.severity]}>Sévérité {SEVERITY_LABEL[a.severity]}</Pill>
          <Pill color={col}>{st}</Pill>
          {a.lab_confirmed && <Pill color="#1E4631">Confirmé labo</Pill>}
        </div>
        <div className="text-sm mt-1"><strong>{a.cases}</strong> cas suspects · <strong className={a.deaths ? "text-[#C62828]" : ""}>{a.deaths}</strong> décès{a.location ? ` · ${a.location}` : ""}{a.onset_date ? ` · début ${fmtDate(a.onset_date)}` : ""}</div>
        <div className="text-xs text-[#5C6B61] mt-1">{a.hospital_name}{a.province ? ` · ${a.province}` : ""} · déclaré par {a.created_by_name} le {fmtDateTime(a.created_at)}</div>
        <p className="text-sm mt-2 bg-[#FDFBF7] rounded-lg p-3">{a.description}</p>
        {a.notification && (
          <div className="text-xs mt-2 inline-flex items-center gap-1" style={{ color: a.notification.sent ? "#2E7D32" : "#E3A72F" }}>
            <Mail className="w-3 h-3" /> {a.notification.sent ? `Email envoyé à ${a.notification.recipients_count} compte(s) Ministère/DPS` : `Email non envoyé (${a.notification.error || "aucun destinataire"})`}
          </div>
        )}
        {a.timeline?.length > 1 && (
          <ul className="mt-2 text-xs text-[#5C6B61] space-y-0.5">
            {a.timeline.slice(1).map((t, i) => <li key={i}>· {fmtDateTime(t.at)} — <strong>{t.by}</strong> : {t.note || t.event}</li>)}
          </ul>
        )}
      </div>
      {actions && <div className="flex flex-col gap-2">{actions}</div>}
    </div>
  );
}

export default function EpidemicAlerts() {
  const { user } = useApp();
  const canDeclare = ["hospital_admin", "doctor", "nurse"].includes(user?.role);
  const canClose = ["hospital_admin", "doctor"].includes(user?.role);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ diseases: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ disease: "cholera", cases: 1, deaths: 0, severity: "modere", location: "", onset_date: "", description: "", lab_confirmed: false });
  const [updFor, setUpdFor] = useState(null);
  const [upd, setUpd] = useState({ cases: 0, deaths: 0, note: "" });

  const load = () => axios.get(`${API}/epidemic-alerts`).then((r) => setData(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); axios.get(`${API}/epidemic-alerts/diseases`).then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/epidemic-alerts`, { ...form, cases: Number(form.cases), deaths: Number(form.deaths), onset_date: form.onset_date || null });
      toast.success(r.data.notification?.sent ? `Alerte transmise au Ministère (${r.data.notification.recipients_count} destinataire(s))` : "Alerte enregistrée (email non envoyé)");
      setOpen(false); setForm({ disease: "cholera", cases: 1, deaths: 0, severity: "modere", location: "", onset_date: "", description: "", lab_confirmed: false }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const close = async (a) => {
    const note = window.prompt("Motif de clôture (fin de flambée, fausse alerte…) :", "Fin de l'épisode");
    if (note === null) return;
    try { await axios.patch(`${API}/epidemic-alerts/${a.id}/close`, { note }); toast.success("Alerte clôturée"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const update = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API}/epidemic-alerts/${updFor.id}/update`, null, { params: upd }); toast.success("Chiffres mis à jour"); setUpdFor(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  if (!data) return <div className="text-[#5C6B61]">Chargement…</div>;
  const s = data.stats;

  return (
    <div className="space-y-6" data-testid="epidemic-page">
      <PageTitle icon={AlertTriangle} title="Alertes épidémiques" subtitle="Déclaration immédiate des cas suspects à la DPS et au Ministère de la Santé (SIMR)."
        action={canDeclare && <Button className="bg-[#C62828] hover:bg-[#A31F1F] text-white" onClick={() => setOpen(true)} data-testid="alert-new-btn"><Plus className="w-4 h-4 mr-2" /> Déclarer une alerte</Button>} />

      <div className="p-4 bg-white rounded-xl border-l-4 border-[#D96C4A] flex gap-3 text-sm text-[#5C6B61]">
        <Info className="w-5 h-5 text-[#D96C4A] shrink-0 mt-0.5" />
        <div><strong className="text-[#1A1D1A]">Comment ça marche :</strong> dès la déclaration, l'alerte apparaît <strong>instantanément</strong> sur le tableau de bord du Ministère et de la DPS de votre province, et un <strong>email</strong> leur est envoyé. Elle doit ensuite être confirmée par votre Zone de Santé selon la procédure SIMR (notification immédiate &lt; 24 h pour les maladies à potentiel épidémique).</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Alertes ouvertes" value={s.open} icon={AlertTriangle} color="#C62828" testid="kpi-open" />
        <Kpi label="Prises en compte" value={s.acknowledged} icon={Landmark} color="#E3A72F" testid="kpi-ack" />
        <Kpi label="Cas suspects actifs" value={s.cases_open} icon={AlertTriangle} color="#D96C4A" sub={`${s.deaths_open} décès`} testid="kpi-cases" />
        <Kpi label="Clôturées" value={s.closed} icon={CheckCircle2} color="#2E7D32" testid="kpi-closed" />
      </div>

      <Section title="Historique des alertes de l'hôpital" icon={AlertTriangle} testid="alerts-list">
        {data.alerts.length === 0 ? <Empty>Aucune alerte déclarée.</Empty> : (
          <div className="divide-y divide-[#E5EBE7]">
            {data.alerts.map((a) => (
              <AlertCard key={a.id} a={a} actions={a.status !== "closed" && canDeclare && (<>
                <Button size="sm" variant="outline" onClick={() => { setUpdFor(a); setUpd({ cases: a.cases, deaths: a.deaths, note: "" }); }} data-testid={`alert-update-${a.id}`}>Mettre à jour</Button>
                {canClose && <Button size="sm" variant="outline" className="text-[#2E7D32]" onClick={() => close(a)} data-testid={`alert-close-${a.id}`}><XCircle className="w-4 h-4 mr-1" /> Clôturer</Button>}
              </>)} />
            ))}
          </div>
        )}
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Déclarer une alerte épidémique</DialogTitle></DialogHeader>
          <form className="space-y-3" onSubmit={submit} data-testid="alert-form">
            <div><Label>Maladie / événement</Label>
              <Select value={form.disease} onValueChange={(v) => setForm({ ...form, disease: v })}><SelectTrigger data-testid="alert-disease"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{meta.diseases.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Cas suspects</Label><Input type="number" min="1" required value={form.cases} onChange={(e) => setForm({ ...form, cases: e.target.value })} data-testid="alert-cases" /></div>
              <div><Label>Décès</Label><Input type="number" min="0" value={form.deaths} onChange={(e) => setForm({ ...form, deaths: e.target.value })} data-testid="alert-deaths" /></div>
              <div><Label>Sévérité</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}><SelectTrigger data-testid="alert-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(SEVERITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Localisation (commune, aire de santé)</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Kalamu, Q. Yolo" data-testid="alert-location" /></div>
              <div><Label>Date des premiers cas</Label><Input type="date" value={form.onset_date} onChange={(e) => setForm({ ...form, onset_date: e.target.value })} data-testid="alert-onset" /></div>
            </div>
            <div><Label>Description clinique</Label><Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Symptômes, évolution, mesures déjà prises…" data-testid="alert-description" /></div>
            <div className="flex items-center gap-2"><Checkbox id="lab" checked={form.lab_confirmed} onCheckedChange={(v) => setForm({ ...form, lab_confirmed: !!v })} data-testid="alert-lab" /><label htmlFor="lab" className="text-sm">Au moins un cas confirmé par le laboratoire</label></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button type="submit" className="bg-[#C62828] text-white" data-testid="alert-save">Transmettre au Ministère</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!updFor} onOpenChange={(v) => !v && setUpdFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mise à jour — {updFor?.disease_label}</DialogTitle></DialogHeader>
          <form className="space-y-3" onSubmit={update} data-testid="alert-update-form">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Cas cumulés</Label><Input type="number" min="0" value={upd.cases} onChange={(e) => setUpd({ ...upd, cases: e.target.value })} data-testid="upd-cases" /></div>
              <div><Label>Décès cumulés</Label><Input type="number" min="0" value={upd.deaths} onChange={(e) => setUpd({ ...upd, deaths: e.target.value })} data-testid="upd-deaths" /></div>
            </div>
            <div><Label>Note d'évolution</Label><Input value={upd.note} onChange={(e) => setUpd({ ...upd, note: e.target.value })} data-testid="upd-note" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setUpdFor(null)}>Annuler</Button><Button type="submit" className="bg-[#1E4631] text-white" data-testid="upd-save">Enregistrer</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
